import { useState } from 'react'
import { Pencil } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useUpdateOwner } from '../../hooks/backend/owners'
import { writeErrorMessage } from '../../utils/writeError'
import { type OwnerRow, OWNER_STATUS_OPTIONS, OWNER_MODEL_OPTIONS } from '../../utils/owners'

function nullIfEmpty(value: string): string | null {
  const t = value.trim()
  return t === '' ? null : t
}

export function EditOwnerDialog({
  owner,
  onSaved,
}: {
  owner: OwnerRow
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const update = useUpdateOwner()

  // Edit the application's own status. On the board, a car in the fleet shows
  // as Live whatever the application says, because the vehicle register is the
  // source record; application_status is what is stored.
  const storedStatus = owner.application_status ?? owner.current_status
  const [currentStatus, setCurrentStatus] = useState(storedStatus)
  const [ownerModel, setOwnerModel] = useState(owner.owner_model ?? '')
  const [notes, setNotes] = useState(owner.notes ?? '')
  const [phone, setPhone] = useState(owner.primary_phone ?? '')
  const [email, setEmail] = useState(owner.primary_email ?? '')

  function reseed() {
    setCurrentStatus(storedStatus)
    setOwnerModel(owner.owner_model ?? '')
    setNotes(owner.notes ?? '')
    setPhone(owner.primary_phone ?? '')
    setEmail(owner.primary_email ?? '')
  }

  async function handleSave() {
    const patch = {
      current_status: currentStatus,
      owner_model: nullIfEmpty(ownerModel),
      notes: nullIfEmpty(notes),
    }
    const person_patch = {
      primary_phone: nullIfEmpty(phone),
      primary_email: nullIfEmpty(email),
    }
    try {
      await update.trigger({ application_id: owner.application_id, patch, person_patch }).result
      toast.success('Owner updated')
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not update owner', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reseed()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit owner</DialogTitle>
          <DialogDescription>{owner.full_name || 'Unnamed owner'}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="own-status">Stage</Label>
            <NativeSelect
              id="own-status"
              value={currentStatus}
              onChange={(e) => setCurrentStatus(e.target.value)}
            >
              {OWNER_STATUS_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="own-model">Track</Label>
            <NativeSelect
              id="own-model"
              value={ownerModel}
              onChange={(e) => setOwnerModel(e.target.value)}
            >
              <option value="">—</option>
              {OWNER_MODEL_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
          </div>

          <p className="text-xs text-muted-foreground sm:col-span-2">
            Changing the track moves this application to that track. Any other
            applications this person has stay on their own tracks.
            {owner.application_status && owner.application_status !== owner.current_status
              ? ' The board shows this owner as ' +
                (owner.current_status === 'onboarded' ? 'Live' : owner.current_status.replace(/_/g, ' ')) +
                ' because the vehicle register says so; that overrides the stage stored here.'
              : ''}
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="own-phone">Primary phone</Label>
            <Input id="own-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="own-email">Primary email</Label>
            <Input id="own-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="own-notes">Notes</Label>
            <Textarea
              id="own-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={update.loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={update.loading}>
            {update.loading && <Spinner className="h-4 w-4" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
